import React, { useRef, useState, useLayoutEffect } from 'react';
import { useLanguage } from '../LanguageContext';

interface ArticleBaseProps {
  id?: string | number;
  title: string;
  date?: string;
  category?: string;
  type?: 'scientific' | 'opinion' | 'informative';
  lineWidth?: string | { en: string; nl: string };
  children?: React.ReactNode;
}

const ArticleBase: React.FC<ArticleBaseProps> = ({ id, title, date, category, type = 'informative', lineWidth, children }) => {
  const { language } = useLanguage();

  const getTypeColor = (t: 'scientific' | 'opinion' | 'informative') => {
    switch (t) {
      case 'scientific': return 'var(--color-alt-accent)';
      case 'opinion': return 'var(--color-main-accent)';
      case 'informative': return 'var(--color-deep-red)';
    }
  };

  const computedLineWidth = lineWidth
    ? (typeof lineWidth === 'object' ? (language === 'nl' ? lineWidth.nl : lineWidth.en) : lineWidth)
    : '8rem';
  // measurement for the signature line to match last visual line of the title
  const titleRef = useRef<HTMLSpanElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    let remeasureTimer: number | null = null;
    const measure = () => {
      const range = document.createRange();
      range.selectNodeContents(el);
      const rects = Array.from(range.getClientRects());
      if (rects.length === 0) return setMeasuredWidth(null);
      const lastRect = rects[rects.length - 1];
      setMeasuredWidth(Math.round(lastRect.width));
    };

    measure();

    // Re-measure after fonts load (prevents incorrect width when font hasn't loaded yet)
    if (document && (document as any).fonts && (document as any).fonts.ready) {
      (document as any).fonts.ready.then(() => {
        measure();
      }).catch(() => {});
    }

    // Extra safe delayed re-measure in case of late layout shifts
    remeasureTimer = window.setTimeout(() => measure(), 150);

    window.addEventListener('resize', measure);
    // also re-measure on window load to catch any late-loading resources
    window.addEventListener('load', measure);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('load', measure);
      if (remeasureTimer) {
        clearTimeout(remeasureTimer);
      }
    };
  }, [title]);

  const signatureWidth = measuredWidth ? `${measuredWidth + 8}px` : computedLineWidth;

  return (
    <article className="mx-auto max-w-6xl py-6 bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] rounded-md" style={{fontFamily: 'Short Stack, cursive'}}>
      <header className="mb-6">
        <h1 className="text-3xl md:text-4xl font-medium mb-2" style={{ fontFamily: 'Short Stack, cursive' }}>
          <span ref={titleRef} style={{ display: 'inline-block', whiteSpace: 'pre-line' }}>{title}</span>
        </h1>
        {/* Signature colored line below title. Flat, same as the /articles
            previews: the global -1deg tilt on .h-1.rounded-full makes a
            line this wide visibly climb toward its right end. translateY
            matches the previews' gap under the title. */}
        <div className="h-1 rounded-full mb-4 transition-all duration-300" style={{ backgroundColor: getTypeColor(type), width: signatureWidth, transform: 'translateY(-2px)' }} />
        {date && <div className="text-sm text-[var(--color-text-muted)]">{new Date(date).toLocaleDateString()}</div>}
        {category && <div className="text-sm text-[var(--color-text-muted)]">{category}</div>}
      </header>

      <section className="prose prose-lg prose-invert max-w-none flex flex-col gap-y-6 text-base md:text-lg" style={{lineHeight: 1.75}}>
        {children}
      </section>
    </article>
  );
};

export default ArticleBase;
